import React, { useState } from 'react';
import dayjs from 'dayjs';

import LoadingButton from '@mui/lab/LoadingButton';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import { useTranslation, TFunction } from 'react-i18next';
import { RefreshGridDataType } from './FeatureParameterGrid';
import { GetFeatureParameterDataType, FeatureParameterDataType, SelectDataType } from './FeatureParameter';
import { DownloadFile } from '../../molecules/CSVExport/CSVDownload';
import { SpeedKind } from '../../../Constant';

const useStyles = makeStyles(
  createStyles({
    buttonStyle: {
      width: '100%',
    },
  })
);

type Props = {
  refreshGridData: RefreshGridDataType;
  selectData: SelectDataType;
  targetName: string;
};

type rowData = {
  day: string[];
  fData: FeatureParameterDataType[];
};

const FeatureParameterCreateCSV = (
  refreshGridData: RefreshGridDataType,
  t: TFunction<'translation'>
): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    // 四捨五入
    const roundDecimal = (value: string, n: number): string => {
      var num = Number(value);
      var ans = Math.round(num * Math.pow(10, n)) / Math.pow(10, n);

      return value === '' ? '' : String(ans);
    };

    const Header1Data = (data: FeatureParameterDataType): string => {
      if (data.Datas.length === 1) {
        return `${data.DeviceInfo.MesName} ${data.DeviceInfo.ItemName}`;
      } else if (data.Datas.length === 3) {
        let head = `${data.DeviceInfo.MesName} ${data.DeviceInfo.ItemName}`;
        return `${head},${head},${head}`;
      } else if (data.Datas.length === 5) {
        let head = `${data.DeviceInfo.MesName} ${data.DeviceInfo.ItemName}`;
        return `${head},${head},${head},${head},${head}`;
      } else {
        return '';
      }
    };

    const Header2Data = (data: FeatureParameterDataType): string => {
      if (data.Datas.length === 1) {
        return t('FeatureParameterGrid.瞬時値', '瞬時値');
      } else if (data.Datas.length === 3) {
        if (data.Datas[0].ItemSpeed === SpeedKind.SPEED_CONAN) {
          return `${t('FeatureParameterGrid.振動最大値', '最大値')},${t(
            'FeatureParameterGrid.振動実行値',
            '実行値'
          )},${t('FeatureParameterGrid.振動速度', '速度')}`;
        } else {
          return `${t('FeatureParameterGrid.電流最大値', '最大値')},${t(
            'FeatureParameterGrid.電流実行値',
            '実行値'
          )},${t('FeatureParameterGrid.電流OA値', 'OA値')}`;
        }
      } else if (data.Datas.length === 5) {
        return `${t('FeatureParameterGrid.振動最大値', '最大値')},${t('FeatureParameterGrid.振動実行値', '実行値')},${t(
          'FeatureParameterGrid.振動OA値',
          'OA値'
        )},${t('FeatureParameterGrid.振動速度', '速度')},${t('FeatureParameterGrid.振動変位', '変位')}`;
      } else {
        return '';
      }
    };

    const BodyData = (fpData: FeatureParameterDataType, index: number): string => {
      if (index > fpData.Datas[0].FeatureParameterDatas.length) {
        return '';
      }
      if (fpData.Datas.length === 1) {
        return roundDecimal(fpData.Datas[0].FeatureParameterDatas[index].Value, 4);
      } else if (fpData.Datas.length === 3) {
        return `${roundDecimal(fpData.Datas[0].FeatureParameterDatas[index].Value, 4)},${roundDecimal(
          fpData.Datas[1].FeatureParameterDatas[index].Value,
          4
        )},${roundDecimal(fpData.Datas[2].FeatureParameterDatas[index].Value, 4)}`;
      } else if (fpData.Datas.length === 5) {
        return `${roundDecimal(fpData.Datas[0].FeatureParameterDatas[index].Value, 4)},${roundDecimal(
          fpData.Datas[1].FeatureParameterDatas[index].Value,
          4
        )},${roundDecimal(fpData.Datas[2].FeatureParameterDatas[index].Value, 4)},${roundDecimal(
          fpData.Datas[3].FeatureParameterDatas[index].Value,
          4
        )},${roundDecimal(fpData.Datas[4].FeatureParameterDatas[index].Value, 4)}`;
      } else {
        return '';
      }
    };

    var selectData = refreshGridData.selectDataType;
    var time = dayjs(new Date(selectData.year, selectData.month - 1, 1, 0, 0, 0)).toDate();
    GetFeatureParameterDataType(selectData.devIndex, time)
      .then((featureParameter) => {
        var dList: string[] = [];
        if (featureParameter.length !== 0) {
          for (var i in featureParameter[0].Datas[0].FeatureParameterDatas) {
            dList.push(dayjs(featureParameter[0].Datas[0].FeatureParameterDatas[i].Day).format('MM/DD'));
          }
        }

        var dayList: rowData = {
          day: dList,
          fData: featureParameter,
        };

        var header1s = `,${featureParameter?.map((item) => Header1Data(item)).join(',')}\n`;
        header1s = header1s.replace(/\r?\n/g, '') + '\n';
        var header2s = `${selectData.year + t('FeatureParameterExportCSV.年', '年')},${featureParameter
          ?.map((item) => Header2Data(item))
          .join(',')}`;
        header2s = header2s.replace(/\n/g, '') + '\n';
        var bodys = dayList?.day?.map((item, index) => {
          return `${item},${dayList?.fData
            ?.map<string>((row2) => {
              return BodyData(row2, index);
            })
            .join(',')}\n`;
        });

        resolve([header1s, header2s].concat(bodys));
      })
      .catch((reason) => reject(reason));
  });
};

const FeatureParameterExportCSV: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);

  const handleCSVClick = () => {
    setLoading(true);
    FeatureParameterCreateCSV(props.refreshGridData, t).then((csvs: string[]) => {
      let bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      let blob = new Blob([bom, ''].concat(csvs), { type: 'text/csv' });
      let filename = `${props.targetName}_${props.selectData.year}${('00' + props.selectData.month).slice(-2)}.csv`;
      DownloadFile(filename, blob);
      setLoading(false);
    });
  };

  return (
    <LoadingButton
      className={classes.buttonStyle}
      loading={loading}
      loadingPosition="start"
      variant="contained"
      color="primary"
      onClick={handleCSVClick}
    >
      {t('FeatureParameterExportCSV.CSV出力', 'CSV出力')}
    </LoadingButton>
  );
};

export default FeatureParameterExportCSV;
